import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '@tyndo/ui-kit';
import { AuthShell, AuthError, AuthNotice } from './AuthShell';
import { register, AuthApiError } from '../auth/nativeAuth';
import { recordError } from '../observability/telemetry';

/**
 * Self-serve registration: company name + email + password (+ confirm) ->
 * POST /api/v1/auth/register -> "check your email to verify" confirmation.
 * No session token is issued until the email is verified.
 */
export default function SignupPage() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!companyName.trim()) {
      setError('Please enter your company name');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your work email');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        companyName: companyName.trim(),
        email: email.trim(),
        password,
      });
      setRegisteredEmail(result.email || email.trim());
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not create your account';
      setError(message);
      recordError('auth.register_failed', err, {
        statusCode: err instanceof AuthApiError ? err.statusCode : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  if (registeredEmail) {
    return (
      <AuthShell title="Check your email">
        <div className="space-y-4">
          <AuthNotice>
            We sent a verification link to <strong>{registeredEmail}</strong>.
            Open it to verify your email and finish setting up your workspace.
          </AuthNotice>
          <p className="text-sm text-muted-foreground">
            Didn't get it? Check your spam folder, or you can request a new link
            from the sign-in page.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Start your free trial" subtitle="Create your workspace">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthError message={error} />}

        <div className="space-y-1.5">
          <Label htmlFor="signup-company">Company name</Label>
          <Input
            id="signup-company"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Manufacturing"
            disabled={loading}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-email">Work email</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            disabled={loading}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            disabled={loading}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-confirm">Confirm password</Label>
          <Input
            id="signup-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter your password"
            disabled={loading}
          />
        </div>

        <Button type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? 'Creating your account…' : 'Create account'}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground text-center mt-4">
        Already have an account?{' '}
        <Button
          variant="link"
          type="button"
          onClick={() => navigate('/login')}
          className="h-auto p-0 text-foreground font-medium underline"
        >
          Sign in
        </Button>
      </p>
    </AuthShell>
  );
}
